import React, { lazy, Suspense, useEffect, useState } from 'react'
import DeleteModalActions from './DeleteSurveyTemplate'
import { useNavigate } from 'react-router-dom'
import ROUTES from '../../consts/paths'
import GenericButton from '../../components/GenericButton'
import { ButtonType } from '../../components/GenericButton/styles'
import { useAppDispatch } from '../../store'
import { getSurveyList } from '../../store/slices/SurveySlice/apis'
import { isSurveyListLoading, surveyListDetails } from '../../store/slices/SurveySlice/selectors'
import { type Row, type ApplyFiltersAndDispatchParams } from '../../components/CustomTable/types'
import { type GridSortItem } from '@mui/x-data-grid'
import ArchiveSurveyTemplate from './ArchiveSurveyTemplate'
import { setEditableSurvey } from '../../store/slices/SurveyTemplateSlice'
import { type ISurveyTemplateRowDataType } from '../../models/SurveyTemplate'
import { type CustomBreadcrumbT } from '../../utils/BreadcrumbDataPreparation'
import PageHeader from '../../components/PageHeader'
import PAGE_TITLES from '../../consts/titles'
import { useTheme } from '@mui/material/styles'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import { Box } from '@mui/material'

export interface AdminSurveyFeatureProps {
  breadcrumb: CustomBreadcrumbT[]
}

const AppTable = lazy(async () => await import('../../components/CustomTable'))
const AdminSurveyFeature: React.FC<AdminSurveyFeatureProps> = ({ breadcrumb }): JSX.Element => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const theme = useTheme()

  const isLoading = isSurveyListLoading()

  const list = surveyListDetails()

  const headerName = list?.header?.headerName || 'Surveys'

  const [perPageSize, setPerPageSize] = useState<number>(15)
  const [search, setSearch] = useState<string>('')
  const [sortModel, setSortModel] = useState<GridSortItem>()
  const [scope, setScope] = useState<'my' | 'all'>('my')

  useEffect(() => {
    const params = { pageSize: perPageSize }
    void dispatch(getSurveyList({ params }))
  }, [dispatch])

  const handleCreateSurvey = (): void => {
    navigate(ROUTES.SURVEY_LIST)
  }
  const extraColumnProps = {
    width: 260
  }
  const extraModalProps = {
    maxWidth: 'md'
  }

  const applyFiltersAndDispatch = async ({
    pageSize,
    pageNumber,
    search,
    sort: sortModel,
    scope
  }: ApplyFiltersAndDispatchParams): Promise<void> => {
    const sort = sortModel && { field: sortModel.field, direction: sortModel.sort }
    const params = {
      search,
      sort: sortModel ? JSON.stringify(sort) : undefined,
      pageSize,
      pageNumber,
      scope
    }
    void dispatch(getSurveyList({ params }))
  }

  const handleRowsPerPageChange = async (pageSize: number): Promise<void> => {
    setPerPageSize(pageSize)
    await applyFiltersAndDispatch({ pageSize, search, sort: sortModel, scope })
  }

  const handleSearch = async (value: string): Promise<void> => {
    setSearch(value)
    await applyFiltersAndDispatch({ pageSize: perPageSize, search: value, sort: sortModel, scope })
  }

  const handlePageChange = async (page: number): Promise<void> => {
    await applyFiltersAndDispatch({ pageSize: perPageSize, pageNumber: page, search, sort: sortModel, scope })
  }

  const onSortModelChange = async (sortModel: GridSortItem): Promise<void> => {
    setSortModel(sortModel)
    await applyFiltersAndDispatch({ pageSize: perPageSize, search, sort: sortModel, scope })
  }

  const handleScopeChange = async (newScope: 'my' | 'all'): Promise<void> => {
    setScope(newScope)
    await applyFiltersAndDispatch({ pageSize: perPageSize, search, sort: sortModel, scope: newScope })
  }

  const onEditClick = (selectedRow: Row): void => {
    const templateId = selectedRow?.data?.templateId
    dispatch(setEditableSurvey(selectedRow?.data))
    navigate(`${ROUTES.SURVEY_LIST}/${templateId as string}`)
  }

  return (
    <Box display='flex' flexDirection='column' gap={6}>
      <PageHeader
        title={PAGE_TITLES.MY_SURVEYS}
        description='All your survey templates are listed below'
        breadcrumb={breadcrumb}
        controls=
        {<GenericButton
          label=' Create Template'
          buttonType={ButtonType.Primary}
          onClick={() => { handleCreateSurvey() }}
          style={{
            flexShrink: 0,
            [theme.breakpoints.down('md')]: {
              width: '100%'
            }
          }}
        />}
      />
      <Suspense fallback={<LoadingPageV2 title={PAGE_TITLES.MY_SURVEYS} />}>
          <AppTable
            loading={isLoading}
            list={list}
            featureName="Survey"
            deleteAction={<DeleteModalActions />}
            archiveAction={<ArchiveSurveyTemplate />}
            extraColumnProps={extraColumnProps}
            extraModalProps={extraModalProps}
            onSearch={(text: string) => { void handleSearch(text) }}
            onSortModelChange={onSortModelChange}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
            onEditClick={onEditClick}
            params={{ scope }}
            setParams={(newParams) => {
              if (newParams.scope && newParams.scope !== scope) {
                void handleScopeChange(newParams.scope as 'my' | 'all')
              }
            }}
            showScopeToggle={true}
            scopeToggleTitle={headerName}
          />
      </Suspense>
    </ Box>
  )
}

export default AdminSurveyFeature
